import React, { useState, useEffect, useMemo } from 'react';
import {
  Col,
  Tooltip,
  Button,
  Modal,
  Icon,
  Row,
  Tag,
  Badge,
  Divider
} from 'antd';
import ListRulesComponent from './ListRulesComponent';
import ImpressionsDetailsPopover from './supporting/ImpressionsDetailsPopover';
import RulesContainer from '../../../../shared/containers/RulesContainer';
import { contentResolutionLabel } from '../../../../../services/contents';
import { Trans, useTranslation } from 'react-i18next';
import {
  calculateValueByCpm,
  hasAccessRole,
  isCpmiCompany,
  SeparateNumberFormat,
  getDomain
} from '../../../../../services/utils';
import {
  getAmountAudienceDisplay,
  getAppliedVolumeDiscount,
  getCpmByStatusCampaign,
  getCpmiByStatusCampaign,
  getImpressionsPerDay,
  getMinimumPlayouts,
  getMultiplyByStatus,
  getRelationShowDays,
  getSpotsPerDay,
  getTotalBonusSpots
} from '../../../../../services/display';
import {
  impressions,
  spots
} from '../../services/investmentCalculations';
import {
  checkIsNewDisplayInEdit,
  isProposalCampaign
} from '../../../../../services/campaignManager';
import GenerateSkeletonComponent from '../../../../shared/components/GenerateSkeletonComponent';
import ModalEditCampaignManagerComponent from './supporting/ModalEditCampaignManagerComponent';
import UserRoleEnum from '../../../../../constants/UserRoleEnum';
import HideWhenCampaignActive from '../../containers/HideWhenCampaignActiveContainer';
import ImpressionManagementComponent from './BudgetTransfer/ImpressionManagementComponent';
import CustomInputCurrency from '../../../../shared/components/CustomInputCurrency';
import HelpTooltipIcon, { HelpIcon } from '../../../../shared/components/HelpTooltipIcon';
import PendingContentButton from '../../../../shared/components/PendingContentButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExchangeAlt } from '@fortawesome/free-solid-svg-icons';
import Colors from '../../../../../themes/Colors';
import TagsDiscount from '../../../../shared/components/TagsDiscount';
import { aspectRatiosMatch } from '../../../../../services/contents';
import VolumeDiscountTooltipContent from './supporting/VolumeDiscountTooltipContent';
import PlayoutsLabelContainer from '../../containers/step2_settings/PlayoutsLabelContainer';
import SpotStatusTag from './SpotStatusTag';
import RelationMediaPreview from './RelationMediaPreview';
import EditableSpotsIndicator from './supporting/EditableSpotsIndicator';
import ModalContentComponent from '../../../../shared/components/ModalContentComponent';
import NumberFormat from '../../../../shared/components/NumberFormat';
import SubsidizeButton from '../../../../shared/components/SubsidizeButton';
import SubsidizedStatusTag from '../../../../shared/components/SubsidizedStatusTag';

function SliderNextArrow(props) {
  const { className, onClick } = props;
  return <div className={className} style={{ right: '15px', zIndex: '1' }} onClick={onClick} />;
}

function SliderPrevArrow(props) {
  const { className, onClick } = props;
  return <div className={className} style={{ left: '6px', zIndex: '1' }} onClick={onClick} />;
}

const RelationCpmRow = ({
  cpmiDisplay,
  appliedDiscount,
  hasCpmiDiscount,
  multiplier,
  currency,
  display,
  isSubsidized,
  isInactive,
  canSeeSubsidized,
  editCampaignActive,
  paidImpressions,
  bonusImpressions,
  subsidizedImpressions,
  onToggleSubsidized
}) => {
  const { t } = useTranslation();
  // The volume discount doesn't lower the rate, it grants extra spots — so
  // `baseCpmi` already covers those bonus impressions. Subsidized ones are the
  // free delivery on top that drags the real cost per impression down.
  const baseCpmi = hasCpmiDiscount ? appliedDiscount.final_cpmi : cpmiDisplay;
  const cpmi = isSubsidized
    ? 0
    : impressions.effectiveCpmi(
        baseCpmi,
        paidImpressions + bonusImpressions,
        subsidizedImpressions
      );
  // Covers both ways the price drops: subsidized impressions and the whole relation bonified.
  const showsEffective = cpmi < baseCpmi;
  return (
    <>
      <div className="textGroup textGroupRelation" style={{ margin: 0 }}>
        {t('Effective CPM')}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        {hasCpmiDiscount && (
          <Tooltip
            placement="left"
            align="center"
            title={
              <VolumeDiscountTooltipContent
                original={cpmiDisplay.toFixed(2)}
                final={(appliedDiscount.final_cpm / multiplier).toFixed(2)}
                currency={currency}
              />
            }
          >
            <Icon
              type="tags"
              style={{ fontSize: '18px', verticalAlign: 'middle', color: '#52C41A' }}
            />
          </Tooltip>
        )}
        <div
          className="screenCardTotalImpacts"
          style={{
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            whiteSpace: 'nowrap',
            height: 'auto',
            color: hasCpmiDiscount ? '#52c41a' : undefined
          }}
        >
          {/* Always mounted so the row can grow into it instead of jumping.
              The negative margin cancels the flex gap while collapsed. */}
          <span
            style={{
              textDecoration: 'line-through',
              color: Colors.textMuted,
              display: 'inline-block',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              maxWidth: showsEffective ? '160px' : 0,
              opacity: showsEffective ? 1 : 0,
              marginRight: showsEffective ? 0 : '-4px',
              transition: 'max-width 0.3s ease-in-out, opacity 0.3s ease-in-out, margin 0.3s ease-in-out'
            }}
          >
            <NumberFormat value={baseCpmi} currency={currency} />
          </span>
          <span style={{ fontWeight: 600, color: hasCpmiDiscount ? '#52c41a' : undefined }}>
            <NumberFormat value={cpmi} currency={currency} />
          </span>
          {t('CPM')}
        </div>
        {canSeeSubsidized && !editCampaignActive && (
          <SubsidizeButton
            tooltip={t('Subsidize ad tooltip')}
            checked={isSubsidized}
            disabled={isInactive}
            onClick={onToggleSubsidized}
          />
        )}
        {editCampaignActive && isSubsidized && (
          <SubsidizedStatusTag suspended={display.promised_shows == 0} />
        )}
      </div>
    </>
  );
};

const RelationSliderComponent = props => {
  const {
    display,
    Slider,
    customContents,
    reportSingleData,
    loadingSingleReport,
    currentCampaign,
    editCampaignActive,
    showModalDeleteRelation,
    toggleFillScreen,
    togglePendingContent,
    date_to,
    date_from,
    programmatic,
    currency,
    campaignCurrency,
    updateImpressionsDisplay,
    updateContentName,
    updateListOfContentsInCampaign,
    user,
    allDisplaysAreOwn,
    updateSubsidizedPrice,
    applyRelationOnScreens,
    cart,
    isPaymentStatusApproved,
    setActive,
    setIsCampaignPaused,
    updatePromisedShowsSubsidized,
    cpmDisplay,
    audience,
    loadingAudience,
    updateCustomContents,
    getTooltipForFixedBudget,
    enabledRenderRules,
    creationType,
    toleranceResolution,
    initialBackup,
    hasInitialBackup
  } = props;

  const domain = getDomain(window.location.hostname);


  const [activeSlides, setActiveSlides] = useState(0);
  const [totalPromisedShowsAllRelations, setTotalPromisedShowsAllRelations] = useState(0);
  const [indexOpen, setindexOpen] = useState(null);
  const [transferModalVisiblePreview, setTransferModalVisiblePreview] = useState(null);
  const [infoModalRelationId, setInfoModalRelationId] = useState(null);

  const isNewDisplay = checkIsNewDisplayInEdit(display.id, initialBackup, hasInitialBackup);

  const isProposal = isProposalCampaign({ editCampaignActive, currentCampaign, creationType });


  const multiplier = getMultiplyByStatus(editCampaignActive, currentCampaign, display);
  const cpmiDisplay = getCpmiByStatusCampaign(editCampaignActive, currentCampaign, display);
  // Backend-provided impacts for the whole screen, so every slide shows the same
  // number. Traditional has no per-spot investment to split them by.
  const displayImpacts = getAmountAudienceDisplay(audience, display.id, true);
  // Traditional buys screen time, not spots: promised_shows is null, so the
  // "minimum playouts" figure comes from the screen's own loop rate and lit hours.
  const minimumPlayouts = getMinimumPlayouts(display, date_from, date_to);

  const { t } = useTranslation();

  var sliderSettings = {
    arrows: true,
    dots: false,
    infinite: false,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    nextArrow: <SliderNextArrow />,
    prevArrow: <SliderPrevArrow />
  };

  const stopPropagation = event => {
    event.stopPropagation();
  };

  // Variable to show the modal of the contentDisplay when applying rules
  const showModalRules = (relation = null) => {
    setSelectRelationForRules(relation ? relation.relationId : null);
  };

  const [selectRelationForRules, setSelectRelationForRules] = useState(null);

  /**
   * Calculates the number of shows based on the promised shows and the report data when the campaigns is running.
   * @param {Object} relation - The relation object containing `promised_shows` and `content.id`.
   * @param {Object} reportSingleData - The report data object containing an array of reports.
   * @param {Object} display - The display object containing `id`.
   * @returns {number} The calculated value of shows.
   */
  function calculateShows(relation) {
    // Check if reportSingleData and report exist
    if (reportSingleData && reportSingleData.report) {
      // Find the element matching the display.id and relation.content.id
      const foundElement = reportSingleData.report.find(
        element => element.display === display.id && element.content === relation.content.id
      );
      // If the element is found, return its shows value subtracted from promised_shows
      if (foundElement) {
        const total = relation.promised_shows + (relation.promised_shows_subsidized || 0);
        if (total === 0) return 0;
        const consumedPaid = Math.floor(foundElement.shows * relation.promised_shows / total);
        return relation.promised_shows - consumedPaid;
      }
    }
    // If the element is not found or there is no data, return promised_shows
    return relation.promised_shows;
  }

  function calculateSubsidizedShows(relation) {
    if (reportSingleData && reportSingleData.report) {
      const foundElement = reportSingleData.report.find(
        element => element.display === display.id && element.content === relation.content.id
      );
      if (foundElement) {
        const total = relation.promised_shows + (relation.promised_shows_subsidized || 0);
        if (total === 0) return 0;
        const consumedPaid = Math.floor(foundElement.shows * relation.promised_shows / total);
        const consumedSubsidized = foundElement.shows - consumedPaid;
        return Math.max(0, (relation.promised_shows_subsidized || 0) - consumedSubsidized);
      }
    }
    return relation.promised_shows_subsidized || 0;
  }

  /**
   * Calculates the maximum value for the InputNumber component.
   * @param {string} paymentStatus - The payment status of the current campaign.
   * @param {Object} relation - The relation object containing `relationId`.
   * @param {Object} reportSingleData - The report data object.
   * @returns {number} The calculated maximum value.
   */
  function calculateMax(relation) {
    let displayId = display.id;
    let paymentStatus = currentCampaign ? currentCampaign.payment_status : null;
    if (editCampaignActive && paymentStatus === 'approved') {
      const totalCustomContentShows = customContents
        .filter(customContent => customContent.displayId === displayId)
        .filter(customContentRelation => customContentRelation.relationId !== relation.relationId)
        .reduce(
          (sum, customContentRelation) =>
            sum +
            (customContentRelation.promised_shows_update_in_transfer
              ? customContentRelation.promised_shows_update_in_transfer
              : customContentRelation.promised_shows),
          0
        );
      const reportShows =
        reportSingleData &&
        reportSingleData.report &&
        reportSingleData.report.find(
          element => element.display === displayId && element.content === relation.content.id
        )
          ? reportSingleData.report.find(
              element => element.display === displayId && element.content === relation.content.id
            ).shows
          : 0;
      return totalPromisedShowsAllRelations - totalCustomContentShows - reportShows;
    }
    return 999999999; // Arbitrary large number as a fallback maximum value
  }

  const handleContent = (value, content) => {
    updateListOfContentsInCampaign({ ...content, name: value });
    customContents
      .filter(customContent => customContent.content.id === content.id)
      .forEach(relation => updateContentName(relation.relationId, content, value, 'single'));
  };

  /**
   * Handles the change event for the InputNumber component.
   *
   * @param {number} newValue - Nuevas salidas restantes (ya convertidas)
   * @param {Object} relation - The relation object.
   */
  function handleChange(newValue, relation) {
    let displayId = display.id;
    const additionalShows =
      reportSingleData && reportSingleData['report']
        ? (reportSingleData['report'].find(
            element =>
              element['display'] === displayId && element['content'] === relation['content']['id']
          ) || {})['shows'] || 0
        : 0;
    updateImpressionsDisplay(displayId, relation.relationId, newValue + additionalShows);
  }

  // Update proposal of relations
  function handleChangeProposal(e, relation, ePrev) {
    if (e === ePrev) return;
    let displayId = display.id;
    updateImpressionsDisplay(displayId, relation.relationId, spots.fromBudget(e, cpmDisplay));
  }

  const updateSubsidizedPriceRelation = relationId => {
    const isSubsidized = relations
      .filter(item => item.relationId === relationId)
      .every(item => !item.subsidized_price);
    updateSubsidizedPrice(relationId, isSubsidized, 'relation');
  };

  const handleIsCampaignsPausedToggle = (isCampaignPaused, relation) => {
    const active = isCampaignPaused ? 0 : 1;
    if (!isCampaignPaused) setIsCampaignPaused(false);
    setActive(relation.relationId, active);
  };

  const fixedBudget = customContentUpdate => {
    const customContentsAux = customContents.map(customContent => {
      if (customContent.relationId === customContentUpdate.relationId) {
        return {
          ...customContent,
          isFixedBudget: !customContentUpdate.isFixedBudget
        };
      }
      return customContent;
    });
    updateCustomContents(customContentsAux);
  };

  useEffect(() => {
    setTotalPromisedShowsAllRelations(
      customContents
        .filter(customContent => customContent.displayId === display.id)
        .reduce((sum, customContent) => sum + customContent.promised_shows, 0)
    );
  }, []);

  const updateActiveSlide = currentSlide => {
    setActiveSlides(currentSlide);
  };

  var sliderSettingsInstance = {
    ...sliderSettings,
    afterChange: current => updateActiveSlide(current)
  };

  const relations = useMemo(() => {
    // In edit mode, ALWAYS show content already assigned to this display regardless of
    // tolerance. Tolerance only controls what NEW content can be added, not what gets
    // removed from existing assignments. The only way to remove content is manual replace.
    // We keep the filter for creation mode (editCampaignActive=false) where it's correct.
    return customContents
      .filter(item => item.displayId === display.id)
      .filter(item => {
        if (!editCampaignActive) {
          // Creation mode: apply tolerance filter normally
          if (!item.content || item.isPendingContent) return true;
          const tolerance = toleranceResolution || 0;
          const match = aspectRatiosMatch(
            item.content.width,
            item.content.height,
            display.resolution_width,
            display.resolution_height,
            tolerance
          )[0];

          return match;
        }
        // Edit mode: always show assigned content, never filter by tolerance
        return true;
      });
  }, [customContents, display, editCampaignActive, currentCampaign.tolerance_resolution]);

  const getTooltipDays = (date_from, date_to, rules) => {
    const days = getRelationShowDays(date_from, date_to, rules);
    return days + ' ' + (days === 1 ? t('day') : t('days'));
  };

  if (relations.length === 0) return null;

  // cpmi companies buy 100% by impressions: no projected playouts anywhere.
  const isCpmi = isCpmiCompany(user);
  const cpmiSuffix = isCpmi ? '_cpmi' : '';
  const canSeeSubsidized =
    !!user &&
    (hasAccessRole(user.roles, [UserRoleEnum.superAdmin, UserRoleEnum.superModeratorAgency]) ||
      allDisplaysAreOwn);
  // Always available once the campaign is paid; otherwise it's noise on proposals
  // and on a brand-new campaign whose display still has a single ad.
  const showImpressionsDetails =
    isPaymentStatusApproved || (!isProposal && (editCampaignActive || relations.length > 1));
  // Returns the appropriate status Tag for a spot, given its status and activity.

  const findContentDisplayForRelation = (relation, contentsDisplays) => {
    return contentsDisplays.find(
      cd =>
        cd.id === relation.relationId &&
        cd.display_id === relation.displayId &&
        cd.content_id === relation.content.id
    );
  };

  return (
    <Slider {...sliderSettingsInstance}>
      {relations.map((relation, index) => {
        const spotValue =
          Math.floor(
            parseFloat(
              calculateValueByCpm(cpmDisplay, relation.promised_shows, 'calculate_price')
            ) * 100
          ) / 100 || 0;
        const isExactSize =
          relation.content.width === display.resolution_width &&
          relation.content.height === display.resolution_height;
        const isInactive = relation.active === 0;
        const isSubsidizedRelation = !!relation.subsidized_price;
        const originalCpm = getCpmByStatusCampaign(editCampaignActive, currentCampaign, display);
        // const discountApplied = relation.programmatic_volume_discount_apply;
        // Calculate total spots per day from ALL relations of this display for discount calculation
        const displayTotalSpotsPerDay = relations.reduce((total, rel) => {
          return total + getSpotsPerDay(rel, date_from, date_to);
        }, 0);
        // Real campaign total (sum of promised_shows) — used for "total" discount thresholds
        const displayTotalPromisedShows = relations.reduce(
          (total, rel) => total + (rel.promised_shows || 0),
          0
        );
        // Calculate total bonus using display-level total spots
        const totalBonus = getTotalBonusSpots(
          display,
          relation,
          date_from,
          date_to,
          originalCpm,
          displayTotalSpotsPerDay,
          displayTotalPromisedShows
        );
        // Calculate proportional bonus for this relation
        const relationSpotsPerDay = getSpotsPerDay(relation, date_from, date_to);
        const proportionalTotalBonus =
          displayTotalSpotsPerDay > 0
            ? Math.round(totalBonus * (relationSpotsPerDay / displayTotalSpotsPerDay))
            : 0;

        const proportionalBonusImpressions = impressions.fromSpots(
          proportionalTotalBonus,
          multiplier
        );
        // Same values the indicators below show, so the CPM can't contradict them.
        const paidImpressions = impressions.fromSpots(calculateShows(relation), multiplier);
        const subsidizedImpressions = impressions.fromSpots(
          isPaymentStatusApproved
            ? calculateSubsidizedShows(relation)
            : relation.promised_shows_subsidized || 0,
          multiplier
        );
        // Discount applies at the display level (honoring discount_type daily/total)
        const appliedDiscount = getAppliedVolumeDiscount(
          display,
          displayTotalSpotsPerDay,
          displayTotalPromisedShows
        );
        const hasCpmiDiscount = !!(
          appliedDiscount &&
          appliedDiscount.final_cpmi &&
          !isPaymentStatusApproved
        );
        const daysTooltip = <span>{getTooltipDays(date_from, date_to, relation.rules)}</span>;
        // Same lock as the investment input: a fixed budget can't be moved from here either.
        const isSpotsEditDisabled = isPaymentStatusApproved || relation.isFixedBudget;
        const bonusChip = value =>
          value > 0 && (!isPaymentStatusApproved || isNewDisplay) ? (
            <div
              className="content-info-relation content-info bonus-indicator"
              style={{
                backgroundColor: Colors.tintSuccess,
                color: '#52c41a',
                margin: 0,
                fontSize: '12px'
              }}
            >
              +{SeparateNumberFormat(value)}
            </div>
          ) : null;
        const perDayBox = (value, label, orange) => (
          <div
            className={
              'content-info-relation content-info' +
              (orange ? ' orange appear-animation-without-movement' : '')
            }
            style={{
              margin: '0 0 10px',
              float: 'left',
              ...(!orange &&
                domain === 'dv360' && {
                  color: Colors.primaryAccent,
                  background: Colors.extraLightPrimary
                })
            }}
          >
            {SeparateNumberFormat(value)} {label}
          </div>
        );
        const deliveredSpotsBlock = (
          <EditableSpotsIndicator
            label={<PlayoutsLabelContainer remaining={isPaymentStatusApproved} />}
            promisedShows={
              programmatic === 'programmatic' ? calculateShows(relation) : minimumPlayouts
            }
            extra={bonusChip(proportionalTotalBonus)}
            editable={programmatic === 'programmatic' && !isPaymentStatusApproved}
            disabled={isSpotsEditDisabled}
            editTitle={t('Modify playouts')}
            warningKey="Modify playouts warning"
            daysTooltip={daysTooltip}
            onApply={newPromisedShows => handleChange(newPromisedShows, relation)}
          />
        );
        const contentDisplay = findContentDisplayForRelation(
          relation,
          currentCampaign.contents_displays
        );
        const spotStatusTag = contentDisplay ? (
          <SpotStatusTag
            revisionStatus={contentDisplay.revision_status}
            rejectReason={contentDisplay.reject_reason || ''}
            isActive={relation.active}
            isPendingContent={relation.isPendingContent}
          />
        ) : null;
        const subsidizedSpotsBlock = (
          <EditableSpotsIndicator
            label={
              isPaymentStatusApproved ? (
                t('Remaining subsidized spots')
              ) : (
                <PlayoutsLabelContainer variant="subsidized" />
              )
            }
            promisedShows={
              isPaymentStatusApproved
                ? calculateSubsidizedShows(relation)
                : relation.promised_shows_subsidized || 0
            }
            editable={!isPaymentStatusApproved}
            disabled={isInactive || isPaymentStatusApproved}
            editTitle={t('Modify subsidized playouts')}
            warningKey="Modify subsidized playouts warning"
            daysTooltip={daysTooltip}
            max={999999999}
            onApply={newPromisedShows =>
              updatePromisedShowsSubsidized(relation.relationId, newPromisedShows, 'single')
            }
          />
        );
        return (
          <React.Fragment key={relation.relationId}>
            {/* Layout de cada anuncio: preview a la izquierda, datos a la derecha
                desde 768px; apilado en mobile. */}
            <div
              className="content-slider-displays relationSlide"
              style={{ marginTop: '15px', position: 'relative' }}
            >
              {!enabledRenderRules && spotStatusTag && (
                <div style={{ position: 'absolute', top: '5px', right: '10px', zIndex: 1 }}>
                  {spotStatusTag}
                </div>
              )}
              <Col
                xs={24}
                sm={24}
                md={programmatic != 'programmatic' ? 12 : 8}
                lg={programmatic != 'programmatic' ? 12 : 8}
                xl={programmatic != 'programmatic' ? 12 : 6}
                className="relationPreviewCol"
              >
                {creationType === 'makeProposal' ||
                  (!relation.isPendingContent && (
                    <div
                      className="inline-container screenContainer"
                      style={{ margin: '5px auto 10px', gap: "5px" }}
                    >
                      <span className='textGroup' style={{ marginBottom: 0, fontWeight: 'bold', marginRight: '0px', fontSize: '18px' }}>{t('Ad')}</span>
                      <Badge
                        count={relation.content.content_version_name || 'V1'}
                        style={{ backgroundColor: isInactive ? Colors.grayMedium : Colors.primary, color: isInactive ? Colors.white : Colors.onPrimary, fontSize: '14px', height: '20px', minWidth: '20px', lineHeight: '20px' }}
                      />
                    </div>
                  ))}
                <div style={{ textAlign: '-webkit-center' }}>
                  {activeSlides >= 0 && activeSlides < relations.length && (
                    <RelationMediaPreview
                      relation={relation}
                      display={display}
                      programmatic={programmatic}
                    />
                  )}
                </div>
                <div style={{ textAlign: 'center' }}>
                  
                  <Tooltip title={t('Spot resolution')}>
                    <span className={'circleRelation'}>
                      {contentResolutionLabel(relation.content, t)}
                    </span>
                  </Tooltip>
                  {!relation.isPendingContent && !isProposal && (
                    <Tooltip title={t('Ad information')}>
                      <Button
                        shape="circle"
                        className="buttonActionsGroup"
                        size="x-small"
                        onClick={() => setInfoModalRelationId(relation.relationId)}
                      >
                        <Icon type="info-circle" />
                      </Button>
                    </Tooltip>
                  )}
                  <Tooltip
                    title={!relation.fill_screen ? t('Expand ad') : t('Return original resolution')}
                  >
                    <Button
                      shape="circle"
                      className="buttonActionsGroup"
                      size="x-small"
                      onClick={() =>
                        toggleFillScreen(
                          relation.displayId,
                          relation.relationId,
                          !relation.fill_screen,
                          'single'
                        )
                      }
                      disabled={isExactSize}
                    >
                      {!relation.fill_screen ? (
                        <Icon type="fullscreen" />
                      ) : (
                        <Icon type="fullscreen-exit" />
                      )}
                    </Button>
                  </Tooltip>
                  <PendingContentButton
                    editCampaignActive={editCampaignActive}
                    currentCampaign={currentCampaign}
                    creationType={creationType}
                    user={user}
                    onClick={() =>
                      togglePendingContent(
                        display.id,
                        relation.relationId,
                        !relation.isPendingContent,
                        'single'
                      )
                    }
                    isPendingContent={relation.isPendingContent}
                  />
                  {relation.indice_relation === null &&
                  editCampaignActive &&
                  currentCampaign.payment_status !== 'pending' &&
                  currentCampaign.payment_status !== 'cancelled' &&
                  currentCampaign.status !== 'proposal' &&
                  user ? (
                    <Tooltip
                      title={relation.active === 1 ? t('Pause content') : t('Resume content')}
                    >
                      <Button
                        shape="circle"
                        className="buttonActionsGroup"
                        size="x-small"
                        onClick={() =>
                          handleIsCampaignsPausedToggle(relation.active === 1, relation)
                        }
                      >
                        <Icon type={relation.active === 1 ? 'pause' : 'caret-right'} />
                      </Button>
                    </Tooltip>
                  ) : null}
                  <HideWhenCampaignActive contentId={relation.content.id} displayId={display.id}>
                    {relations.length > 1 && !isInactive && (
                      <Tooltip placement="top" title={t('Remove spot')}>
                        <Button
                          shape="circle"
                          size="x-small"
                          className="buttonActionsGroup"
                          onClick={() =>
                            showModalDeleteRelation(
                              'deleteRelation',
                              display.id,
                              relation.relationId
                            )
                          }
                        >
                          <Icon type="close-square" />
                        </Button>
                      </Tooltip>
                    )}
                  </HideWhenCampaignActive>
                </div>
              </Col>
              <Col
                xs={24}
                sm={24}
                md={programmatic != 'programmatic' ? 12 : 16}
                lg={programmatic != 'programmatic' ? 12 : 16}
                xl={programmatic != 'programmatic' ? 12 : 18}
                className="relationDataCol"
              >
                {programmatic === 'programmatic' && (
                  <Col
                    xs={24}
                    sm={24}
                    md={24}
                    lg={24}
                    xl={24}
                    className="relationInvestmentCol"
                  >
                    <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                      <div
                        style={{
                          marginBottom: '5px'
                        }}
                      >
                        <RelationCpmRow
                          cpmiDisplay={cpmiDisplay}
                          appliedDiscount={appliedDiscount}
                          hasCpmiDiscount={hasCpmiDiscount}
                          multiplier={multiplier}
                          currency={editCampaignActive ? campaignCurrency : currency}
                          display={display}
                          isSubsidized={isSubsidizedRelation}
                          isInactive={isInactive}
                          canSeeSubsidized={canSeeSubsidized}
                          editCampaignActive={editCampaignActive}
                          paidImpressions={paidImpressions}
                          bonusImpressions={proportionalBonusImpressions}
                          subsidizedImpressions={subsidizedImpressions}
                          onToggleSubsidized={() =>
                            updateSubsidizedPriceRelation(relation.relationId)
                          }
                        />
                      </div>
                      <div className="textGroup textGroupRelation">{t('Investment')}</div>
                      <div className="relationInvestmentInput">
                        <Tooltip
                          placement="left"
                          title={<span>{getTooltipDays(date_from, date_to, relation.rules)}</span>}
                        >
                          <span style={{ width: '50%' }}>
                            <CustomInputCurrency
                              currency={editCampaignActive ? campaignCurrency : currency}
                              value={spotValue}
                              style={{ width: '100%' }}
                              onChange={e => handleChangeProposal(e, relation, spotValue)}
                              disabled={
                                isPaymentStatusApproved || relation.isFixedBudget || isInactive
                              }
                              step={(cpmDisplay / 1000).toFixed(2)}
                            />
                          </span>
                        </Tooltip>
                        {showImpressionsDetails && (
                          <ImpressionsDetailsPopover
                            reportSingleData={reportSingleData}
                            customContents={customContents}
                            display={display}
                            relation={relation}
                            currentCampaign={currentCampaign}
                            editCampaignActive={editCampaignActive}
                            cpmDisplay={cpmDisplay}
                            multiplier={multiplier}
                          />
                        )}
                        {editCampaignActive && currentCampaign.payment_status === 'approved' ? (
                          <Tooltip placement="top" title={`${t('Budget transfer')}`}>
                            <Button
                              shape="circle"
                              className={'buttonActionsGroup ' + (isNewDisplay ? 'highlight' : '')}
                              onClick={() => setTransferModalVisiblePreview(relation.relationId)}
                              style={{ marginLeft: '5px' }}
                            >
                              <FontAwesomeIcon
                                icon={faExchangeAlt}
                                style={{ color: isNewDisplay ? Colors.atention : Colors.primaryAccent }}
                              />
                            </Button>
                          </Tooltip>
                        ) : null}
                        {!isPaymentStatusApproved && !isInactive && (
                          <Tooltip
                            placement="top"
                            title={getTooltipForFixedBudget('groupDisplay', relation.isFixedBudget)}
                          >
                            <Button
                              shape="circle"
                              className="buttonActionsGroup"
                              style={{ marginLeft: '5px' }}
                              onClick={event => {
                                fixedBudget(relation);
                                stopPropagation(event);
                              }}
                            >
                              {
                                <i
                                  className={relation.isFixedBudget ? 'fa fa-lock' : 'fa fa-unlock'}
                                  aria-hidden="true"
                                  style={{
                                    color: 'gray',
                                    cursor: 'pointer',
                                    fontSize: 'large'
                                  }}
                                ></i>
                              }
                            </Button>
                          </Tooltip>
                        )}
                      </div>
                    </Col>
                    <Col
                      xl={24}
                      lg={24}
                      md={24}
                      sm={24}
                      xs={24}
                      style={{ marginTop: '10px', textAlign: 'justify' }}
                    >
                      <Col xl={12} lg={24} md={24} sm={24} xs={24}>
                        {loadingSingleReport ? (
                          <GenerateSkeletonComponent count={1} height={5} width={100} />
                        ) : (
                          <EditableSpotsIndicator
                            label={
                              isPaymentStatusApproved
                                ? t('Remaining impressions')
                                : t('Impressions bought')
                            }
                            promisedShows={calculateShows(relation)}
                            multiplier={multiplier}
                            icon="team"
                            editable={!isPaymentStatusApproved}
                            disabled={isSpotsEditDisabled}
                            editTitle={t('Modify impressions')}
                            warningKey={`Modify impressions warning${cpmiSuffix}`}
                            daysTooltip={daysTooltip}
                            onApply={newPromisedShows => handleChange(newPromisedShows, relation)}
                            extra={bonusChip(proportionalBonusImpressions)}
                          />
                        )}
                        <div
                          style={{
                            textAlign: 'left',
                            marginBottom: '10px',
                            display: 'flex',
                            alignItems: 'center',
                            position: 'relative',
                            width: 'fit-content'
                          }}
                        >
                          <div
                            className="content-info-relation content-info"
                            style={{
                              margin: 0,
                              ...(domain === 'dv360' && {
                                color: Colors.primaryAccent,
                                background: Colors.extraLightPrimary
                              })
                            }}
                          >
                            {SeparateNumberFormat(getImpressionsPerDay(relation, date_from, date_to, multiplier))}{' '}
                            {t('impressions/day')}
                          </div>
                        </div>
                      </Col>
                      {!isCpmi && (
                        <Col xl={12} lg={24} md={24} sm={24} xs={24}>
                          {deliveredSpotsBlock}
                          {perDayBox(getSpotsPerDay(relation, date_from, date_to), t('spots/day'))}
                        </Col>
                      )}
                    </Col>
                    <Col
                      xl={24}
                      lg={24}
                      md={24}
                      sm={24}
                      xs={24}
                      style={{ marginTop: '10px', textAlign: 'justify' }}
                    >
                      <Col xl={12} lg={24} md={24} sm={24} xs={24}>
                        {canSeeSubsidized && (
                          <EditableSpotsIndicator
                            label={
                              <>
                                {isPaymentStatusApproved
                                  ? t('Remaining subsidized impressions')
                                  : t('Subsidized impressions')}
                                <Tooltip
                                  placement="bottomLeft"
                                  title={t(
                                    'These impressions are reflected as subsidize in the campaign'
                                  )}
                                >
                                  <Icon
                                    style={{
                                      fontSize: '18px',
                                      color: Colors.atention,
                                      verticalAlign: 'middle',
                                      marginLeft: '5px'
                                    }}
                                    type="warning"
                                  />
                                </Tooltip>
                              </>
                            }
                            promisedShows={
                              isPaymentStatusApproved
                                ? calculateSubsidizedShows(relation)
                                : relation.promised_shows_subsidized || 0
                            }
                            multiplier={multiplier}
                            icon="team"
                            editable={!isPaymentStatusApproved}
                            disabled={isInactive || isPaymentStatusApproved}
                            editTitle={t('Modify subsidized impressions')}
                            warningKey={`Modify subsidized impressions warning${cpmiSuffix}`}
                            daysTooltip={daysTooltip}
                            max={999999999}
                            onApply={newPromisedShows =>
                              updatePromisedShowsSubsidized(
                                relation.relationId,
                                newPromisedShows,
                                'single'
                              )
                            }
                          />
                        )}
                        {!!relation.promised_shows_subsidized && (
                          <div
                            className="content-info orange content-info-relation appear-animation-without-movement"
                            style={{
                              paddingRight: '16px',
                              paddingLeft: '16px',
                              float: 'left',
                              marginBottom: '10px',
                              marginTop: '0px'
                            }}
                          >
                            {SeparateNumberFormat(
                              getImpressionsPerDay(
                                isPaymentStatusApproved
                                  ? {
                                      ...relation,
                                      promised_shows_subsidized: calculateSubsidizedShows(
                                        relation
                                      )
                                    }
                                  : relation,
                                date_from,
                                date_to,
                                multiplier,
                                'subsidized'
                              )
                            )}{' '}
                            {t('impressions subsidized/day')}
                          </div>
                        )}
                      </Col>
                      {!isCpmi && (
                        <Col xl={12} lg={24} md={24} sm={24} xs={24}>
                          {canSeeSubsidized && subsidizedSpotsBlock}
                          {canSeeSubsidized &&
                            !!relation.promised_shows_subsidized &&
                            perDayBox(
                              getSpotsPerDay(relation, date_from, date_to, 'subsidized'),
                              t('spots subsidized/day'),
                              true
                            )}
                        </Col>
                      )}
                    </Col>
                  </Col>
                )}
                {programmatic !== 'programmatic' && (
                  <>
                    {!isCpmi && (
                      <Col
                        xs={24}
                        sm={24}
                        md={24}
                        lg={24}
                        xl={12}
                        style={{ paddingRight: '5px', marginTop: '15px' }}
                      >
                        {deliveredSpotsBlock}
                      </Col>
                    )}
                    <Col
                      xs={24}
                      sm={24}
                      md={24}
                      lg={24}
                      xl={12}
                      style={{ paddingRight: '5px', marginTop: '15px' }}
                    >
                      {loadingAudience ? (
                        <GenerateSkeletonComponent count={1} height={5} width={100} />
                      ) : (
                        <EditableSpotsIndicator
                          label={t('Impacts')}
                          promisedShows={displayImpacts}
                          icon="eye"
                        />
                      )}
                    </Col>
                  </>
                )}
                {programmatic === 'traditional' &&
                  display.rank_discounts &&
                  display.rank_discounts.length > 0 && (
                    <TagsDiscount
                      display={display}
                      currency={editCampaignActive ? campaignCurrency : currency}
                      color={Colors.atention}
                      backgroundColor="#fffbe6"
                      dateFrom={date_from}
                      dateTo={date_to}
                      withText
                    />
                  )}
              </Col>
              {enabledRenderRules && (
                <Col xs={24} sm={24} md={24} lg={24} xl={24} style={{ textAlign: 'left' }}>
                  <Divider style={{ margin: '10px 0' }} />
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      gap: '10px'
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <ListRulesComponent
                        listContentDisplay={[relation]}
                        type="relation"
                        styleRules={{
                          container: {
                            justifyContent: 'flex-start',
                            maxWidth: '100%',
                            width: '100%'
                          },
                          chip: { width: '100%', marginLeft: 0, marginRight: 0 },
                          badges: { display: 'flex', flexWrap: 'wrap', alignItems: 'center' }
                        }}
                        defaultText={t('Without rules')}
                        showModalRules={() => showModalRules(relation)}
                      />
                    </div>
                    {spotStatusTag}
                  </div>
                </Col>
              )}
            </div>
            {selectRelationForRules === relation.relationId && (
              <Modal
                key={relation.relationId}
                title={
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    {t('Rules in campaign')}
                    <HelpTooltipIcon
                      href="https://www.youtube.com/watch?v=VuoK7wMlcJc"
                      tooltip={
                        <Trans
                          i18nKey="Rules tooltip"
                          components={{
                            icon: <HelpIcon />
                          }}
                        />
                      }
                    />
                  </div>
                }
                visible={selectRelationForRules === relation.relationId}
                footer={[
                  <Button key="ok" type="primary" onClick={() => showModalRules()}>
                    {t('Close')}
                  </Button>
                ]}
                onCancel={() => showModalRules()}
                width={800}
              >
                <RulesContainer
                  key={display.id}
                  typeModal="contentDisplay"
                  workingHours={display.working_hours}
                  typeName={relation.content.name}
                  listContentDisplay={[relation]}
                />
              </Modal>
            )}
            {infoModalRelationId === relation.relationId && (
              <ModalContentComponent
                key={relation.relationId}
                visible
                content={relation.content}
                onChangeName={value => handleContent(value, relation.content)}
                disabled={isInactive}
                onCancel={() => setInfoModalRelationId(null)}
              />
            )}
            {indexOpen === index && (
              <ModalEditCampaignManagerComponent
                visible={indexOpen === index}
                onCancel={() => setindexOpen(null)}
                applyRelationOnScreens={applyRelationOnScreens}
                groupedDisplays={[display]}
                groupName={relation.content.name}
                fromModal={'relation'}
                // filterByContent={relation.id}
              />
            )}
            {transferModalVisiblePreview === relation.relationId && (
              <Modal
                key={relation.relationId}
                title={t('Budget transfer')}
                onCancel={() => setTransferModalVisiblePreview(null)}
                visible={true}
                footer={null}
                width={1400}
                style={{ top: '10px' }}
              >
                <ImpressionManagementComponent
                  currentCampaign={currentCampaign}
                  customContents={customContents}
                  reportSingleData={reportSingleData}
                  transferModalVisible={transferModalVisiblePreview}
                  cart={cart}
                  showTransferModal={setTransferModalVisiblePreview}
                  updateImpressionsDisplay={updateImpressionsDisplay}
                  editCampaignActive={editCampaignActive}
                  initialBackup={initialBackup}
                  hasInitialBackup={hasInitialBackup}
                />
              </Modal>
            )}
          </React.Fragment>
        );
      })}
    </Slider>
  );
};

export default RelationSliderComponent;
